import * as React from 'react';

import { useAtom, useAtomValue } from 'jotai';

import {
  $inboundTicketFilter,
  $inboundTicketLoads,
  $outboundLoads,
  $outboundTicketFilter,
} from '~/modules/trade/_state/scaleTicketPro.state';
import { LoadRecord } from '~/modules/trade/types/load';
import { PurchaseLoadRecord } from '~/modules/trade/types/purchaseLoad';
import { TicketListFilter } from '~/modules/trade/types/scaleTickets';

type UseTicketListFilterOptions = {
  direction: 'inbound' | 'outbound';
  mineOnly?: boolean;
  currentUserId?: string;
};

export const useTicketListFilter = ({
  direction,
  mineOnly,
  currentUserId,
}: UseTicketListFilterOptions) => {
  const isInbound = direction === 'inbound';

  const [inboundFilter, setInboundFilter] = useAtom($inboundTicketFilter);
  const [outboundFilter, setOutboundFilter] = useAtom($outboundTicketFilter);
  const inboundQuery = useAtomValue($inboundTicketLoads);
  const outboundQuery = useAtomValue($outboundLoads);

  const filter = isInbound ? inboundFilter : outboundFilter;
  const setFilter = isInbound ? setInboundFilter : setOutboundFilter;
  const query = isInbound ? inboundQuery : outboundQuery;

  const items = React.useMemo(() => {
    if (isInbound) {
      const allLoads = (inboundQuery.data?.purchaseLoads ??
        []) as PurchaseLoadRecord[];
      if (mineOnly && currentUserId) {
        return allLoads.filter((pl) => pl.createdByUser?.id === currentUserId);
      }
      return allLoads;
    }
    const allLoads = (outboundQuery.data?.loads ?? []) as LoadRecord[];
    if (mineOnly && currentUserId) {
      return allLoads.filter(
        (load) => load.createdByUser?.id === currentUserId,
      );
    }
    return allLoads;
  }, [
    isInbound,
    inboundQuery.data?.purchaseLoads,
    outboundQuery.data?.loads,
    mineOnly,
    currentUserId,
  ]);

  const handleSearchChange = React.useCallback(
    (value: string | null) => {
      setFilter((prev: TicketListFilter) => ({
        ...prev,
        searchTerm: value ?? '',
      }));
    },
    [setFilter],
  );

  const handleStatusChange = React.useCallback(
    (value?: string) => {
      setFilter((prev: TicketListFilter) => ({
        ...prev,
        statuses: value === 'all' ? [] : value ? [value] : [],
      }));
    },
    [setFilter],
  );

  const handleStatusToggle = React.useCallback(
    (status: string) => {
      setFilter((prev: TicketListFilter) => {
        const statuses = prev.statuses.includes(status)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status];
        return { ...prev, statuses };
      });
    },
    [setFilter],
  );

  const handleAccountToggle = React.useCallback(
    (id: string, isSelected: boolean) => {
      setFilter((prev: TicketListFilter) => ({
        ...prev,
        accountIds: isSelected
          ? [...prev.accountIds, id]
          : prev.accountIds.filter((aid) => aid !== id),
      }));
    },
    [setFilter],
  );

  const handleClearAccounts = React.useCallback(() => {
    setFilter((prev: TicketListFilter) => ({ ...prev, accountIds: [] }));
  }, [setFilter]);

  return {
    items,
    filter,
    setFilter,
    handleSearchChange,
    handleStatusChange,
    handleStatusToggle,
    handleAccountToggle,
    handleClearAccounts,
    loadingState: query.loadingState,
    refetch: query.refetch,
  };
};
